import { createApp, watch } from 'vue';
import type { Component } from 'vue';
import App from './App.vue';

// Quasar imports
import { Quasar, Dialog, Notify, Loading, SessionStorage, Meta } from 'quasar';
import '@quasar/extras/roboto-font/roboto-font.css';
import '@quasar/extras/material-icons/material-icons.css';
import '@quasar/extras/material-symbols-rounded/material-symbols-rounded.css';
import 'quasar/src/css/index.sass';

// // QCalendar
// import '@quasar/quasar-ui-qcalendar/src/css/calendar-month.sass'

// Date formatting
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

// PrimeVue imports
import PrimeVue from 'primevue/config';
import ToastService from 'primevue/toastservice';
import ConfirmationService from 'primevue/confirmationservice';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import MultiSelect from 'primevue/multiselect';
import Button from 'primevue/button';
import Panel from 'primevue/panel';
import DatePicker from 'primevue/datepicker';
import InputNumber from 'primevue/inputnumber';
import Tooltip from 'primevue/tooltip';
import Message from 'primevue/message';
import 'primeicons/primeicons.css';

// Auto Animate
import { autoAnimatePlugin } from '@formkit/auto-animate/vue';

// ChartJS
import { Chart, registerables } from 'chart.js';

// ApexCharts
import VueApexCharts from 'vue3-apexcharts';

// Observe Visibility
import ObserveVisibility from 'vue3-observe-visibility2';

// i18n
import { createI18n } from 'vue-i18n';
import messages from 'src/i18n';
import { Quasar as QuasarLang } from 'quasar';

// Router
import createRouter from './router';

// Pinia Store
import createStore from './stores';

// Runtime config
import { loadRuntimeConfig } from '@erebus/runtime-config';
import { createPreset } from '@erebus/theme';

// Axios setup
import { setupAxiosInterceptors, initAxios, api, erebus } from './boot/axios';
import { chooseAuthMode } from './boot/authMode';
import { redirectHashUrl } from './boot/webHistoryRedirect';
import { clearSavedBearerAuth } from './boot/session';
import { bootSession } from './boot/bootSession';
import { onAuthFailure } from '@erebus/http';
import { useApplicationStore } from './stores/application';
import { useConnectionStore } from './stores/connection';
import { useAuthStore } from './stores/auth';
import { useMemberStore } from './stores/member';

import '@erebus/theme/status.css';
import '@erebus/theme/elevation.css';
import '@erebus/theme/color-scheme.css';
import '@erebus/theme/invalid-state.css';
import './css/app.scss';

// Filters
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { getDateContext } from './boot/filters';

// Service Dependency Injection
import {
  createServices,
  CallRecordingServiceKey,
  IVRJourneyServiceKey,
  TranscriptionServiceKey,
  CallAuditLogServiceKey,
  VoiceServiceKey,
  ErrorHandlingServiceKey,
  ActivationServiceKey,
  PatientNameServiceKey,
  AudioNoteServiceKey,
} from './services/injection-keys';

/**
 * Main application initialization.
 *
 * Bootstraps the Vue application with all required plugins, stores, and services.
 * Initialization order is important - dependencies must be set up before dependents.
 *
 * @async
 * @throws {Error} If critical initialization fails (will be caught and logged)
 */
async function initApp() {
  // Create Vue app
  const app = createApp(App as Component);

  // ======================
  // 1. RUNTIME CONFIG (must be first)
  // ======================
  // Detect environment from hostname (synchronous, no config file needed)
  const runtimeConfig = loadRuntimeConfig();
  // Old `#/` links become normal paths first: chooseAuthMode() and the router read the path.
  redirectHashUrl();
  // Cookie auth or bearer token, fixed for this page load (see boot/authMode).
  const cookieAuth = chooseAuthMode();
  // Cookie auth drops what a bearer login saved, before Pinia restores it.
  if (cookieAuth) {
    clearSavedBearerAuth();
  }

  // ======================
  // 2. PINIA STORE
  // ======================
  const pinia = createStore();
  app.use(pinia);

  // Store instances (needed for subsequent boot steps)
  const applicationStore = useApplicationStore();
  const authStore = useAuthStore();
  const memberStore = useMemberStore();

  // Set branding from runtime config
  applicationStore.branding = runtimeConfig.branding;
  app.config.globalProperties.$branding = runtimeConfig.branding;

  // ======================
  // 3. AXIOS (must be set up before any API calls)
  // ======================
  // Sets base URLs from runtime config. Bearer mode sends the auth store's
  // token on every request; cookie mode sends the session cookie.
  initAxios(authStore);

  // ======================
  // 4. FILTERS (date/phone formatting)
  // ======================
  app.config.globalProperties.$filters = {
    formatSecondsToTime(seconds: number): string {
      const totalSeconds = Math.abs(Math.floor(seconds));
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const remainingSeconds = totalSeconds % 60;
      const paddedHours = hours.toString().padStart(2, '0');
      const paddedMinutes = minutes.toString().padStart(2, '0');
      const paddedSeconds = remainingSeconds.toString().padStart(2, '0');
      return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
    },
    formatDateTZ(value: string | Date | number | undefined): string {
      if (!value) return '';
      const date = new Date(value);
      if (isNaN(date.getTime())) return '';
      // Use the local timezone for the format
      return formatInTimeZone(
        date,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        'M/d HH:mm:ss XXX',
      );
    },
    formatDate(value: string | Date | number | undefined): string {
      if (!value) return '';
      const date = new Date(value);
      if (isNaN(date.getTime())) return '';
      return format(date, 'MMMM d, yyyy');
    },
    formatDateAlt(value: string | Date | number | undefined): string {
      if (!value) return '';
      const date = new Date(value);
      if (isNaN(date.getTime())) return '';
      return format(date, 'yyyy-MM-dd');
    },
    formatShortDate(value: string | Date | number | undefined): string {
      if (!value) return '';
      const date = new Date(value);
      if (isNaN(date.getTime())) return '';
      return format(date, 'yyyy/MM/dd');
    },
    formatDateTime(value: string | Date | number | undefined): string {
      if (!value) return '';
      const formatObject = getDateContext(value, i18n);
      return formatInTimeZone(
        formatObject.date,
        formatObject.localTimeZone,
        formatObject.formatString,
        { locale: formatObject.locale },
      );
    },
    formatDateTimeTZ(value: string | Date | number | undefined): string {
      if (!value) return '';
      const date = new Date(value);
      return formatInTimeZone(
        date,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        'MMMM d, yyyy H:mm XXX',
      );
    },
    formatShortDateTime(value: string | Date | number | undefined): string {
      if (!value) return '';
      const formatObject = getDateContext(value, i18n);
      return formatInTimeZone(
        formatObject.date,
        formatObject.localTimeZone,
        formatObject.formatString,
        { locale: formatObject.locale },
      );
    },
    formatShortDateTimeTZ(value: string | Date | number | undefined): string {
      if (!value) return '';
      const date = new Date(value);
      return formatInTimeZone(
        date,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        'M/d H:mm XXX',
      );
    },
    formatTime(value: string | Date | number | undefined): string {
      if (!value) return '';
      const formatObject = getDateContext(value, i18n);
      return formatInTimeZone(
        formatObject.date,
        formatObject.localTimeZone,
        formatObject.formatString,
        { locale: formatObject.locale },
      );
    },
    formatMonth(value: string | Date | number | undefined): string {
      if (!value) return '';
      return format(new Date(value), 'MMMM, yyyy');
    },
    formatPhone(value: string): string {
      if (!value) return '';
      const phoneNumber = parsePhoneNumberFromString(value);
      if (phoneNumber === undefined) return value;
      return phoneNumber.formatInternational();
    },
    formatShortDateTimeYear(value: string | Date | number | undefined): string {
      if (!value) return '';
      const formatObject = getDateContext(value, i18n);
      return formatInTimeZone(
        formatObject.date,
        formatObject.localTimeZone,
        formatObject.formatString,
        { locale: formatObject.locale },
      );
    },
  };

  // ======================
  // 5. SENTRY (error tracking)
  // ======================
  // Sentry initialization commented out until environment is set.
  // Uncomment this whole block (and the Sentry import) when ready to use.
  /*
  const getSampleRate = (env: string) => {
    if (env === 'development' || env === 'beta') {
      return 0.25;
    } else {
      return 0.1;
    }
  };
  const env = applicationStore.getDeployment;
  Sentry.init({
    app,
    dsn: 'YOUR_SENTRY_DSN',
    environment: env,
    sampleRate: getSampleRate(env)
  })
  */

  // ======================
  // 6. i18n
  // ======================
  let _locale = localStorage.getItem('locale');
  const branding = applicationStore.branding ? applicationStore.branding : 'praxes';

  if (!_locale || _locale.length === 0 || _locale.includes('null')) {
    _locale = 'en-US';
    if (branding !== 'praxes') {
      _locale = _locale + '-' + branding;
    }
  } else {
    if (_locale.length === 5 && branding !== 'praxes') {
      _locale = _locale + '-' + branding;
    } else if (_locale.length > 5 && branding === 'praxes') {
      _locale = _locale.slice(0, 5);
    }
  }

  const i18n = createI18n({
    legacy: false,
    locale: _locale,
    silentFallbackWarn: true,
    silentTranslationWarn: true,
    messages,
  });

  // Set Quasar language pack
  const qLang = _locale.slice(0, 2) === 'en' ? 'en-US' : 'fr';
  try {
    // Use explicit imports for Quasar language packs (Vite requires this)
    if (qLang === 'en-US') {
      const langModule = await import('quasar/lang/en-US');
      QuasarLang.lang.set(langModule.default);
    } else {
      const langModule = await import('quasar/lang/fr');
      QuasarLang.lang.set(langModule.default);
    }
  } catch (err) {
    console.error('Error loading Quasar language pack:', err);
  }
  localStorage.setItem('locale', _locale);

  app.use(i18n);

  // ======================
  // 6b. FLAG SERVICES + COOKIE SESSION RESTORE
  // ======================
  // The flag and entitlement services are provided first: the restore fetches
  // the flags, and that store injects its service only once (boot/bootSession).
  // Before the router: installing it starts the first navigation, and the
  // guards must already know whether there is a session. Before the axios
  // interceptors too, so a 401 from the restore cannot trigger a logout.
  await bootSession(app, cookieAuth, { authStore, memberStore, applicationStore });

  // ======================
  // 7. ROUTER (needs to be before axios setup for interceptors)
  // ======================
  const router = createRouter();
  app.use(router);

  // ======================
  // 8. QUASAR (needs to be before axios setup for notifications)
  // ======================
  app.use(Quasar, {
    plugins: {
      Dialog,
      Notify,
      Loading,
      SessionStorage,
      Meta,
    },
    config: {
      // Quasar config
    },
  });

  // ======================
  // 9. AXIOS INTERCEPTORS
  // ======================
  // Setup axios with interceptors (requires app, router, and stores to be initialized)
  setupAxiosInterceptors(app, router, { authStore });

  // Registered only after the restore, as the console does, so a page load
  // with no session (a public page, or a signed-out user) is not sent to login.
  if (cookieAuth) {
    onAuthFailure(async () => {
      await authStore.authLogout();
      await router.push({ name: 'login' }).catch(() => {});
    });
  }

  // Export for use in other modules (debug globals)
  /* eslint-disable @typescript-eslint/no-explicit-any */
  (window as any).$api = api;
  (window as any).$medService = erebus;
  /* eslint-enable @typescript-eslint/no-explicit-any */

  // ======================
  // 10. CONNECTION MONITORING
  // ======================
  /**
   * Connection monitoring setup.
   *
   * Manages connectivity state for both backend services (Legacy Java API and Erebus Go API).
   * Uses a three-state model: 'online' | 'degraded' | 'offline'.
   *
   * State transitions are driven reactively:
   * - Axios interceptors record success/failure on every request
   * - Browser online/offline events detect network changes
   * - Visibility change listener pings backends when returning to a stale tab
   *
   * @see stores/connection.ts for the connection store implementation
   */
  const connectionStore = useConnectionStore();

  /**
   * Browser 'online' event handler.
   *
   * Note: navigator.onLine only indicates network connectivity, not internet access.
   * We use it as a hint to trigger a recovery ping rather than directly setting status.
   * This prevents false positives when connected to a network without internet.
   */
  window.addEventListener('online', () => {
    connectionStore.forceRetry();
  });

  /**
   * Browser 'offline' event handler.
   *
   * When the browser reports offline status, we record failures for both backends.
   * This accelerates the transition to degraded/offline state.
   */
  window.addEventListener('offline', () => {
    connectionStore.recordFailure('legacy');
    connectionStore.recordFailure('erebus');
  });

  /**
   * Visibility change handler.
   *
   * When the user returns to a hidden tab and the app is not fully online,
   * fire a recovery ping to check if backends have recovered. This handles
   * the "left tab for 10 minutes, came back" case without a polling loop.
   */
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && connectionStore.appStatus !== 'online') {
      connectionStore.forceRetry();
    }
  });

  /**
   * Connection recovery watcher.
   *
   * Monitors the overall app connection status and triggers data synchronization
   * when recovering from an offline state. This ensures any pending offline changes
   * (activations, messages, medications, cases, iterations) are synced to the server.
   */
  let previousAppStatus = connectionStore.appStatus;

  watch(
    () => connectionStore.appStatus,
    (newStatus) => {
      if (previousAppStatus === 'offline' && newStatus !== 'offline') {
        applicationStore.goOnline();
      }
      previousAppStatus = newStatus;
    },
  );

  // ======================
  // 11. SERVICE DEPENDENCY INJECTION
  // ======================
  // Provide services for dependency injection
  const services = createServices();
  app.provide(CallRecordingServiceKey, services.callRecordingService);
  app.provide(IVRJourneyServiceKey, services.ivrJourneyService);
  app.provide(TranscriptionServiceKey, services.transcriptionService);
  app.provide(CallAuditLogServiceKey, services.callAuditLogService);
  app.provide(ActivationServiceKey, services.activationService);
  app.provide(VoiceServiceKey, services.voiceService);
  app.provide(ErrorHandlingServiceKey, services.errorHandlingService);
  app.provide(PatientNameServiceKey, services.patientNameService);
  app.provide(AudioNoteServiceKey, services.audioNoteService);

  // Feature flags + Entitlements are provided at step 6b (boot/bootSession).

  // ======================
  // 12. AUTH BOOT
  // ======================
  // Cookie mode did this in 6b, before the router.
  if (!cookieAuth && authStore.isAuthenticated) {
    if (!authStore.getUser) {
      try {
        await memberStore.fetchUser();
      } catch (err) {
        console.log(err);
        // Will redirect in router guards
      }
    }

    // Flags are otherwise only fetched after a login, and a page reload skips
    // login entirely - without this every flag reads false for the whole
    // session. Awaited so the first render already knows what is enabled.
    await applicationStore.fetchFeatureFlags();
  }

  // ======================
  // 13. PRIMEVUE
  // ======================
  app.use(PrimeVue, {
    theme: {
      preset: createPreset(runtimeConfig.branding),
      options: {
        darkModeSelector: '.dark-mode',
      },
    },
    // PrimeVue appends overlays to the body and stacks them from these values.
    // The defaults (overlay 1000, modal 1100) sit below a work surface, so a
    // Select opened inside one would render behind it. Raised past the surface
    // (1500) keeping the original order between them, and left below the app
    // header and footer (2000) so nothing else moves.
    zIndex: {
      overlay: 1600,
      menu: 1600,
      modal: 1700,
      tooltip: 1800,
    },
  });
  app.use(ToastService);
  app.use(ConfirmationService);
  // PrimeVue components are registered globally under their library names.
  /* eslint-disable vue/multi-word-component-names, vue/no-reserved-component-names */
  app.component('InputText', InputText);
  app.component('InputNumber', InputNumber);
  app.component('Select', Select);
  app.component('MultiSelect', MultiSelect);
  app.component('Button', Button);
  app.component('Panel', Panel);
  app.component('DatePicker', DatePicker);
  app.directive('tooltip', Tooltip);
  app.component('Message', Message);
  /* eslint-enable vue/multi-word-component-names, vue/no-reserved-component-names */

  // ======================
  // 14. AUTO ANIMATE
  // ======================
  app.use(autoAnimatePlugin);

  // ======================
  // 15. CHARTJS
  // ======================
  Chart.register(...registerables);

  // ======================
  // 16. APEXCHARTS
  // ======================
  app.use(VueApexCharts);

  // ======================
  // 17. OBSERVE VISIBILITY
  // ======================
  app.use(ObserveVisibility);

  // ======================
  // 18. MOUNT APP
  // ======================
  app.mount('#app');
}

// Initialize app
initApp().catch((err) => {
  console.error('Failed to initialize app:', err);
});
